import './App.css';
import {ThemeProvider} from "@mui/material";
import govTheme from "./theme/GovTheme";
import ApiClientLocatorContext, {ApiClientLocator} from "./client/ApiClientLocator";
import {useEffect, useRef} from "react";
import {EnvConfiguration, OAuth2ClientConfig} from "./EnvConfig";
import axios, {AxiosInstance} from "axios";
import {SubmissionApiClient} from "./client/submission/SubmissionApiClient";
import dayjs from "dayjs";
import cs from "dayjs/locale/cs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import {FormSessionApiClient} from "./client/formsession/FormSessionApiClient";
import Keycloak from "keycloak-js";
import BearerTokenRequestInterceptor from "./auth/BearerTokenRequestInterceptor";
import RefreshTokenRequestInterceptor from "./auth/RefreshTokenRequestInterceptor";
import KeycloakAuthProvider from "./auth/keycloak/KeycloakAuthProvider";
import {UserApiClient} from "./client/user/UserApiClient";
import {SnackbarProvider} from "@dain/mui-components";
import AppBody from "./AppBody";
import {SubmissionAuthorizationApiClient} from "./client/submission/SubmissionAuthorizationApiClient";
import {CatalogApiClient} from "./client/catalog/CatalogApiClient";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {PaymentInformationApiClient} from "./client/payment/PaymentInformationApiClient";
import {PersonApiClient} from "./client/person/PersonApiClient";
import {IsdsMessageApiClient} from "./client/message/IsdsMessageApiClient";
import {IsdsMessageStateApiClient} from "./client/message-state/IsdsMessageStateApiClient";
import {StatisticsApiClient} from "./client/statistics/StatisticsApiClient";

function buildClientLocator(backendRestApiUrl: string, backendIsdsRestApiUrl: string, keycloak: Keycloak): ApiClientLocator {
    const axiosInstance = buildAxiosInstance(backendRestApiUrl, keycloak);
    const axiosIsdsInstance = buildAxiosInstance(backendIsdsRestApiUrl, keycloak);

    return {
        catalogClient: new CatalogApiClient(axiosInstance),
        formSessionClient: new FormSessionApiClient(axiosInstance),
        isdsMessageClient: new IsdsMessageApiClient(axiosIsdsInstance),
        isdsMessageStateClient: new IsdsMessageStateApiClient(axiosIsdsInstance),
        personClient: new PersonApiClient(axiosInstance),
        paymentInformationApiClient: new PaymentInformationApiClient(axiosInstance),
        submissionAuthorizationClient: new SubmissionAuthorizationApiClient(axiosInstance),
        submissionClient: new SubmissionApiClient(axiosInstance),
        statisticsClient: new StatisticsApiClient(axiosInstance),
        userClient: new UserApiClient(axiosInstance)
    };
}

function buildAxiosInstance(backendRestApiUrl: string, keycloak: Keycloak): AxiosInstance {
    const axiosInstance = axios.create({
        baseURL: backendRestApiUrl,
    });

    const bearerTokenInterceptor = new BearerTokenRequestInterceptor(keycloak);
    axiosInstance.interceptors.request.use(
        (config) => bearerTokenInterceptor.interceptRequest(config),
        (error) => Promise.reject(error)
    );

    const refreshTokenInterceptor = new RefreshTokenRequestInterceptor(keycloak);
    axiosInstance.interceptors.request.use(
        (config) => refreshTokenInterceptor.interceptRequest(config),
        (error) => Promise.reject(error)
    );

    return axiosInstance;
}

function buildKeycloakClient(clientConfig: OAuth2ClientConfig): Keycloak {
    return new Keycloak({
        url: clientConfig.keycloakUrl,
        realm: clientConfig.realm,
        clientId: clientConfig.clientId,
    });
}

const queryClient = new QueryClient();

export default function App() {

    const envConfig = useRef(EnvConfiguration.getConfig());

    const keycloak = useRef<Keycloak>(buildKeycloakClient(envConfig.current.oauth2));

    const apiClientLocator = useRef<ApiClientLocator>();
    if (!apiClientLocator.current) {
        apiClientLocator.current = buildClientLocator(envConfig.current.serverApiUrl, envConfig.current.serverIsdsApiUrl, keycloak.current);
    }

    useEffect(() => {
        dayjs.extend(localizedFormat);
        dayjs.locale(cs);
    }, []);

    return (
        <ThemeProvider theme={govTheme}>
            <QueryClientProvider client={queryClient} >
                <SnackbarProvider>
                    <KeycloakAuthProvider keycloak={keycloak.current} initOptions={{scope: envConfig.current.oauth2.scope}}>
                        <ApiClientLocatorContext.Provider value={apiClientLocator.current}>
                            <AppBody/>
                        </ApiClientLocatorContext.Provider>
                    </KeycloakAuthProvider>
                </SnackbarProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}
